import { fromMarkdown } from 'mdast-util-from-markdown'
import { toString } from 'mdast-util-to-string'

export const GLOSSARY_LETTERS = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ']

// Terms that start with a digit or a symbol go under '#'
export const getTermLetter = (title: string): string => {
  const first = title.trim().charAt(0).toUpperCase()
  return GLOSSARY_LETTERS.includes(first) ? first : '#'
}

export const groupByLetter = <T extends { title: string }>(terms: T[]): { letter: string; terms: T[] }[] => {
  const sorted = [...terms].sort((a, b) => a.title.localeCompare(b.title, 'en'))
  return GLOSSARY_LETTERS.map((letter) => ({
    letter,
    terms: sorted.filter((term) => getTermLetter(term.title) === letter),
  })).filter((group) => group.terms.length > 0)
}

// First paragraph as plain text: link text stays, markdown syntax goes; the card clamps it to two lines
export const getExcerpt = (markdown: string): string => {
  const paragraph = fromMarkdown(markdown).children.find((node) => node.type === 'paragraph')
  return paragraph ? toString(paragraph) : ''
}

export const SEARCH_MIN_LENGTH = 3

// Text the search matches: the title and the aliases, one per line, never the description
export const getSearchText = ({ title, aliases }: { title: string; aliases: string[] }): string =>
  [title, ...aliases].join('\n').toLowerCase()

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// True when the query starts a word in the text: 'pan' finds 'PAN truncation' but not 'company'.
// Runs of spaces in the query count as one; the text is matched as written
export const matchesQuery = (text: string, query: string): boolean => {
  const normalized = query.trim().toLowerCase().replace(/\s+/g, ' ')
  if (normalized.length < SEARCH_MIN_LENGTH) return false
  return new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(normalized)}`, 'u').test(text.toLowerCase())
}

// Fisher–Yates shuffle; `random` is injectable so tests stay deterministic
export const shuffle = <T>(items: T[], random: () => number = Math.random): T[] => {
  const result = [...items]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

// Splits items into slides of `size` cards each
export const chunk = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, i * size + size))
